import { useEffect, useRef, useState } from "react";
import JobInvoice from "../../crm/jobs/models/JobInvoice";
import FlexRow from "../../../components/base/FlexRow";
import Icon from "../../../components/base/Icon";
import DataItem from "../../../models/DataItem";
import { AmountType, InvoiceItemTypes } from "../../../core/Enums";
import { useEnvironment, useHub } from "../../../core/Global";
import { useParams } from "react-router-dom";
import Indicator from "../../../components/base/Indicator";
import Modal from "../../../components/base/Modal";
import SignaturePad from "signature_pad";
import InvoicePay from "./InvoicePay";

export default function InvoiceView() {

    const { owner, id, token } = useParams()
    const hub = useHub()
    const env = useEnvironment()
    const moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    });

    const [model, modelSet] = useState<JobInvoice>()
    const [loading, loadingSet] = useState<boolean>(true)
    const [expired, expiredSet] = useState<boolean>(true)

    const [signModal, signModalSet] = useState<boolean>(false)
    const [signModalOpened, signModalOpenedSet] = useState<boolean>(false)

    const [payModal, payModalSet] = useState<boolean>(false)

    const [ItemTypes] = useState<DataItem[]>([
        {
            id: "1",
            name: "Product"
        },
        {
            id: "2",
            name: "Service"
        },
        {
            id: "3",
            name: "Hour"
        },
        {
            id: "4",
            name: "Expense"
        }
    ])

    useEffect(() => {
        hub.Get(`/base/public/share/invoice/${owner}/${id}/${token}`, res => {
            if (res.success) {
                modelSet(res.data)
                expiredSet(false)
            }
            else expiredSet(true)
            loadingSet(false)
        })
    }, [])


    const [accepting, acceptingSet] = useState<boolean>(false)
    const acceptInvoice = () => {
        acceptingSet(true);
        hub.Post("/base/public/job/invoice/accept", {
            token: token,
            id: id,
            ownerUserId: owner
        }, res => {
            if (res.success && res.data) {
                modelSet(res.data)
            }
            acceptingSet(false)
        })
    }


    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const padRef = useRef<SignaturePad | null>(null);
    useEffect(() => {

        if (!signModalOpened) return;

        const t = window.setTimeout(() => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;

            const ctx = canvas.getContext("2d");
            ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);

            padRef.current = new SignaturePad(canvas, {
                backgroundColor: "rgb(255,255,255)",
            });
        }, 0);

        return () => {
            window.clearTimeout(t);
            padRef.current?.off();
            padRef.current = null;
        };
    }, [signModalOpened])

    useEffect(() => {
        if (!canvasRef.current) return;

        padRef.current = new SignaturePad(canvasRef.current, {
            backgroundColor: "rgb(255,255,255)",
        });
    }, [canvasRef])

    // What the customer still owes before they can pay: accepting, signing, or both. Signing is
    // accepting as well, so whenever a signature is wanted the button opens the pad. The phone
    // layout used to accept without it, which left the invoice accepted, never signed, and with
    // no way left on the page to sign it or pay.
    const needsSignature = !!model?.signatureRequired && !model?.signatureLog
    const needsAccept = !!model?.acceptRequired && !model?.acceptLog
    const startAccept = () => {
        if (needsSignature) signModalSet(true)
        else acceptInvoice()
    }

    return (
        <div>
            {
                loading ? <Indicator />
                    : expired ?
                        <div className="m-0">
                            <div className="text-center text-danger mb-5">
                                <Icon icon="exclamation-triangle" size={40} type="thin" />
                            </div>
                            <div className="text-center text-danger fs-6">
                                Oops! This link is invalid or has expired.
                            </div>
                        </div>
                        :
                        <div className="m-0">

                            {
                                model?.paid &&
                                <FlexRow className="alert alert-success">
                                    <div className="me-2">
                                        <Icon icon="check-circle" type="solid" size={18} />
                                    </div>
                                    <div>
                                        Payment has been successfully completed for this {model?.issued ? "invoice" : "estimate"}.
                                    </div>
                                </FlexRow>
                            }

                            <div className="mb-5">
                                <FlexRow className="mb-5">
                                    <FlexRow>
                                        <span className="fw-bolder fs-3 text-gray-800">
                                            {model?.issued ? "Invoice" : "Estimate"} {model?.number}
                                        </span>
                                        {
                                            model?.acceptLog &&
                                            <span className="badge badge-light-success ms-2">
                                                <Icon icon="check-circle" type="solid" className="me-1" />
                                                Accepted
                                            </span>
                                        }
                                        {
                                            model?.signatureLog &&
                                            <span className="badge badge-light-primary ms-2">
                                                <Icon icon="signature" type="solid" className="me-1" />
                                                Signed
                                            </span>
                                        }
                                    </FlexRow>
                                    <FlexRow className="d-none d-md-flex ms-auto">

                                        {
                                            model?.paymentEnabled && !model.paid && (!model.acceptRequired || model.acceptLog) && (!model.signatureRequired || model.signatureLog) &&
                                            <button type="button" className="btn btn-primary btn-sm me-2"
                                                onClick={e => payModalSet(true)}>
                                                <Icon icon="credit-card" className="me-2" />
                                                Pay {model?.issued ? "Invoice" : "Estimate"}
                                            </button>
                                        }

                                        <a href={`${env.hubUrl}/base/public/job/invoice/download/${owner}/${model?.id}/${token}`} target="_blank"
                                            className="btn btn-light-primary btn-active-light-primary btn-sm me-2">
                                            <Icon icon="download" className="me-2" />
                                            Download PDF
                                        </a>

                                        {
                                            (needsAccept || needsSignature) &&
                                            <button type="button" className="btn btn-success btn-sm"
                                                onClick={e => startAccept()} disabled={accepting}>
                                                {
                                                    accepting ? <Indicator text="Accepting" />
                                                        : <>
                                                            <Icon icon={needsAccept ? "check" : "signature"} />
                                                            {needsAccept ? "Accept" : "Sign"}
                                                        </>
                                                }
                                            </button>
                                        }

                                        {/* <a href={`${env.hubUrl}/base/public/job/invoice/download/${model?.id}`} target="_blank"
                                className="btn btn-success btn-sm">
                                <Icon icon="credit-card" className="me-2" />
                                Pay {model?.issued ? "Invoice" : "Estimate"}
                            </a> */}
                                    </FlexRow>
                                </FlexRow>

                                <FlexRow className="mb-4">
                                    <div className="me-10">
                                        <div className="fw-bold text-gray-600 fs-7">Name:</div>
                                        <div className="text-gray-800 fs-6">{model?.billingName}</div>
                                    </div>
                                    {
                                        // Left out by the server when the invoice does not include the customer's phone.
                                        model?.billingPhone &&
                                        <div>
                                            <div className="fw-bold text-gray-600 fs-7">Phone:</div>
                                            <div className="text-gray-800 fs-6">{model.billingPhone.phoneFriendly}</div>
                                        </div>
                                    }
                                </FlexRow>

                                <div className="mb-4">
                                    <div className="fw-bold text-gray-600 fs-7">Address:</div>
                                    <div className="text-gray-800 fs-6">{model?.billingAddress}</div>
                                </div>
                            </div>

                            <div className="flex-grow-1">
                                <div className="border-bottom mb-9">
                                    <table className="table mb-3">
                                        <thead>
                                            <tr className="border-bottom fs-6 fw-bolder text-muted">
                                                <th className="pb-2">Description</th>
                                                <th className="text-end pb-2">Qty</th>
                                                <th className="text-end pb-2">Rate</th>
                                                <th className="text-end pb-2">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                model?.items?.map((item, key) =>
                                                    <tr key={key} className="fw-bolder text-gray-700 fs-5 text-end align-items-center">
                                                        <td className="text-start align-middle">
                                                            <div className="pt-6">
                                                                <a href="#" onClick={e => {
                                                                    e.preventDefault();
                                                                    // selectedItemSet(item);
                                                                    // itemFormModalSet(true);
                                                                }}>
                                                                    {item.title}
                                                                </a>
                                                            </div>
                                                            <FlexRow>
                                                                <Icon icon="tag" size={11} />
                                                                <span className="fw-normal fs-7 ms-1">
                                                                    {ItemTypes.filter(f => f.id == item.itemType?.toString())[0].name}
                                                                </span>
                                                            </FlexRow>
                                                            {
                                                                !window.isNullOrEmpty(item.description) &&
                                                                <FlexRow>
                                                                    <Icon icon="info-circle" size={11} />
                                                                    <span className="fw-normal ms-1 fs-7">
                                                                        {item.description}
                                                                    </span>
                                                                </FlexRow>
                                                            }
                                                        </td>
                                                        <td className="pt-6 align-middle">
                                                            {item.qty}
                                                            {
                                                                item.itemType == InvoiceItemTypes.HourlyService &&
                                                                <span className="fs-8 text-muted ms-1">h</span>
                                                            }
                                                        </td>
                                                        <td className="pt-6 align-middle">
                                                            <div>
                                                                {moneyFormatter.format(item.amount ?? 0)}
                                                            </div>
                                                            {
                                                                item.tax &&
                                                                <div className="fs-8 fw-normal">
                                                                    {item.tax.included ? "" : "+"} ({item.tax.name} {item.tax.amountText})
                                                                    {
                                                                        item.tax.included && <Icon icon="circle-info" className="ms-1" size={10} type="solid" title={"Included Tax"} />
                                                                    }
                                                                </div>
                                                            }
                                                        </td>
                                                        <td className="pt-6 text-dark fw-boldest align-middle">
                                                            {moneyFormatter.format(item.totalAmount ?? 0)}
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                                <FlexRow alignItems="start">
                                    <div>
                                        {
                                            model?.note && model.note != "" &&
                                            <div className="fs-8">
                                                Note: {model?.note}
                                            </div>
                                        }
                                        {
                                            model?.signatureLog &&
                                            <div>
                                                <img src={`${env.hubUrl}${model.signatureLog.fileUrl}`} className="w-200px w-md-250px" />
                                            </div>
                                        }
                                    </div>
                                    <div className="w-75 w-md-250px ms-auto">
                                        <div className="d-flex flex-stack mb-3">
                                            <div className="fw-bold pe-10 text-gray-600 fs-7">Subtotal:</div>
                                            <div className="text-end fw-bolder fs-6 text-gray-800">
                                                {moneyFormatter.format(model?.subtotal ?? 0)}
                                            </div>
                                        </div>
                                        {
                                            model?.itemsTotalTax && model?.itemsTotalTax > 0 ?
                                                <div className="d-flex flex-stack mb-3">
                                                    <div className="fw-bold pe-10 text-gray-600 fs-7">
                                                        Items Tax
                                                    </div>
                                                    <div className="text-end fw-bolder fs-6 text-gray-800">
                                                        {moneyFormatter.format(model.itemsTotalTax)}
                                                    </div>
                                                </div>
                                                : <></>
                                        }
                                        {
                                            model?.taxes && model.taxes.length > 0 ?
                                                <>
                                                    {
                                                        model.taxes.map((tax, key) =>
                                                            <div key={key} className="d-flex flex-stack mb-3">
                                                                <div className="fw-bold pe-10 text-gray-600 fs-7">
                                                                    <a href="#" className="me-2" title="Remove Tax"
                                                                        onClick={e => {
                                                                            e.preventDefault();
                                                                            // hub.Post(API_ROUTES.JobInvoices + "taxremove", {
                                                                            //     id: model.id,
                                                                            //     taxId: tax.id
                                                                            // }, res => {
                                                                            //     if (res.success) modelSet(res.data)
                                                                            // })

                                                                        }}>
                                                                        <Icon icon="circle-minus" />
                                                                    </a>
                                                                    {
                                                                        tax.amountType == AmountType.Percent ?
                                                                            <>{tax.nameWithAmount}</>
                                                                            : <>{tax.name}</>
                                                                    }
                                                                </div>
                                                                <div className="text-end fw-bolder fs-6 text-gray-800">
                                                                    {moneyFormatter.format(tax.amount ?? 0)}
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                </>
                                                :
                                                <></>
                                        }

                                        <hr />

                                        <div className="d-flex flex-stack">
                                            <div className="fw-boldest pe-10 text-gray-800 fs-5">Total</div>
                                            <div className="text-end fw-bolder fs-4 text-gray-800">
                                                {moneyFormatter.format(model?.total ?? 0)}
                                            </div>
                                        </div>

                                    </div>
                                </FlexRow>
                            </div>

                            <div className="mt-20 d-block d-md-none">
                                {
                                    (needsAccept || needsSignature) &&
                                    <button type="button" className="btn btn-success w-100 mb-2"
                                        onClick={e => startAccept()} disabled={accepting}>
                                        {
                                            accepting ? <Indicator text="Accepting" />
                                                : <>{needsAccept ? "Accept" : "Sign"}</>
                                        }
                                    </button>
                                }

                                {
                                    model?.paymentEnabled && !model.paid && (!model.acceptRequired || model.acceptLog) && (!model.signatureRequired || model.signatureLog) &&
                                    <button type="button" className="btn btn-primary w-100 mb-2"
                                        onClick={e => payModalSet(true)}>
                                        <Icon icon="credit-card" className="me-2" />
                                        Pay {model?.issued ? "Invoice" : "Estimate"}
                                    </button>
                                }

                                <a href={`${env.hubUrl}/base/public/job/invoice/download/${owner}/${model?.id}/${token}`} target="_blank"
                                    className="btn btn-light-primary btn-active-light-primary w-100">
                                    Download PDF
                                </a>
                            </div>

                        </div>
            }


            {
                signModal &&
                <Modal id="modal-sign-invoice"
                    open visible
                    title={`Sign ${model?.issued ? "Invoice" : "Estimate"}`}
                    buttonText="Sign & Accept"
                    buttonColor="success"
                    onOpen={() => {
                        signModalOpenedSet(true)
                    }}
                    onClose={() => {
                        signModalSet(false)
                        signModalOpenedSet(false)
                    }}
                    onSubmit={async (body, callback) => {

                        if (padRef.current?.isEmpty()) {
                            alert("Empty Signature");
                            callback!()
                            return;
                        }
                        const data = padRef.current?.toDataURL("image/png");

                        if (!data) {
                            alert("Empty Signature");
                            callback!()
                            return;
                        }

                        const file = await fetch(data)
                        const blob = await file.blob()

                        hub.Upload({
                            category: "inv-signatures",
                            file: blob
                        }, res => {
                            if (res.success && res.data) {

                                hub.Post("/base/public/job/invoice/sign", {
                                    token: token,
                                    id: id,
                                    ownerUserId: owner,
                                    signature: res.data
                                }, res => {
                                    if (res.success && res.data) {
                                        modelSet(res.data)
                                    }
                                    acceptingSet(false)
                                    callback!(res)
                                })

                            }
                        })


                    }}
                    secondActionText="Clear Pad"
                    secondActionColor="light"
                    onSecondAction={() => {
                        padRef.current?.clear()
                    }}>

                    <div>
                        {
                            model?.disclaimer &&
                            <div className="mb-4 p-4 rounded" style={{
                                background: "var(--bs-gray-100)",
                                whiteSpace: "pre-line",
                                fontSize: 13
                            }}>
                                {model.disclaimer}
                            </div>
                        }

                        <canvas
                            ref={canvasRef}
                            style={{
                                width: "100%",
                                height: "400px",
                                border: "1px solid #ccc",
                            }}
                        />
                    </div>

                </Modal>
            }

            {
                payModal && model &&
                <Modal id="modal-pay-invoice"
                    visible open noForm staticBackdrop
                    title={`Pay ${model?.issued ? "Invoice" : "Estimate"}`}
                    onClose={() => payModalSet(false)}>
                    <div className="mb-5">
                        <InvoicePay
                            inv={model}
                            onClose={() => {
                                window.closeModal("modal-pay-invoice")
                            }}
                            onPaied={inv => {
                                modelSet(inv)
                                window.closeModal("modal-pay-invoice")
                            }} />
                    </div>
                </Modal>
            }



        </div>
    )
}